import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../api/auth';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedField, ThemedInput, ThemedNotice, ThemedPanel, ThemedSubmitButton, ThemedBottomActionBar } from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';

type ChangePwdForm = {
  old_password: string;
  new_password: string;
  confirm_password: string;
  transaction_password: string;
  security_answer: string;
};

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.6 10.6A2 2 0 0 0 13.4 13.4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.9 5.2A9.4 9.4 0 0 1 12 5c5.5 0 9 7 9 7a15.2 15.2 0 0 1-2 2.8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.6 6.6C4.3 8.2 3 12 3 12s3.5 7 9 7a8.8 8.8 0 0 0 4.4-1.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.1 12S5.7 5 12 5s9.9 7 9.9 7-3.6 7-9.9 7-9.9-7-9.9-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PasswordInput({ value, placeholder, onChange }: { value: string; placeholder: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <ThemedInput
        type={visible ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="pr-11"
      />
      <button
        type="button"
        onClick={() => setVisible((show) => !show)}
        className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
        style={{ color: visible ? C.gold : C.muted }}
        aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
      >
        <PasswordVisibilityIcon visible={visible} />
      </button>
    </div>
  );
}

function ChangePwdHero() {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xl font-semibold leading-none" style={{ color: C.text }}>{t('changePwd.title')}</div>
            <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>{t('changePwd.subtitle')}</div>
          </div>
          <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: `${C.gold}66`, background: C.chipBg, color: C.gold, boxShadow: `0 0 20px ${C.gold}18` }}>
            <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V8a5 5 0 0 1 10 0v3" />
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2" />
            </svg>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

export function ChangePwd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState<ChangePwdForm>({
    old_password: '',
    new_password: '',
    confirm_password: '',
    transaction_password: '',
    security_answer: '',
  });
  const [showSecurityQuestion, setShowSecurityQuestion] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formDisabled = !form.old_password || !form.new_password || !form.confirm_password || !form.transaction_password || submitting;

  function patch(key: keyof ChangePwdForm, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit() {
    setError('');
    setSuccess('');
    if (!form.old_password || !form.new_password || !form.confirm_password || !form.transaction_password) {
      setError(t('changePwd.fillRequired'));
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError(t('changePwd.pwdMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.changePassword(form);
      setSuccess(t('changePwd.successMsg'));
    } catch (e: any) {
      setError(e?.response?.data?.msg || e?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('changePwd.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '112px' }}>
          <ChangePwdHero />

          <ThemedPanel className="p-4">
            <div className="flex flex-col gap-5">
              <ThemedField label={t('changePwd.oldPwd')} required>
                <PasswordInput value={form.old_password} placeholder={t('changePwd.oldPwdPlaceholder')} onChange={(value) => patch('old_password', value)} />
              </ThemedField>

              <ThemedField label={t('changePwd.newPwd')} required>
                <PasswordInput value={form.new_password} placeholder={t('changePwd.newPwdPlaceholder')} onChange={(value) => patch('new_password', value)} />
              </ThemedField>

              <ThemedField label={t('changePwd.confirmPwd')} required>
                <PasswordInput value={form.confirm_password} placeholder={t('changePwd.confirmPwdPlaceholder')} onChange={(value) => patch('confirm_password', value)} />
              </ThemedField>

              <TransactionSecurity
                txPassword={form.transaction_password}
                onTxPasswordChange={(val) => patch('transaction_password', val)}
                showSecurity={showSecurityQuestion}
                onShowSecurityChange={setShowSecurityQuestion}
                securityAnswer={form.security_answer}
                onSecurityAnswerChange={(val) => patch('security_answer', val)}
                colors={C}
              />

              {error && <ThemedNotice tone="error">{error}</ThemedNotice>}
              {success && <ThemedNotice tone="success">{success}</ThemedNotice>}
            </div>
          </ThemedPanel>
        </div>

        <ThemedBottomActionBar>
          <ThemedSubmitButton disabled={formDisabled} onClick={() => void handleSubmit()}>
            {submitting ? '...' : t('changePwd.submit')}
          </ThemedSubmitButton>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

export default ChangePwd;
